import { ProgCss } from "./ProgCss.Generated";
import { ConditionalLog, } from "./tools";
import { getTooltipTargetFromWrapper, } from "./tooltips";

let eventuallyActiveElement: HTMLElement | null = null;
const delayedFocusableAttr = 'data-delayed-focusable';

export const delayedFocus = {
    hasEventuallyActiveElement: () => !!eventuallyActiveElement || !!document.activeElement && document.activeElement !== document.body,
    eventuallyFocus: (element: HTMLElement) => {
        if (element.hasAttribute(delayedFocusableAttr)) {
            eventuallyActiveElement = element;
        } else {
            element.focus();
            eventuallyActiveElement = null;
        }
    },
    checkForEventualFocus: () => {
        ConditionalLog.log("checkForEventualFocus");
        if (eventuallyActiveElement !== null) {
            const focusableWithin = focusableChoicesWithin(eventuallyActiveElement);
            if (focusableWithin[0]) {
                focusableWithin[0].focus();
                eventuallyActiveElement = null;
            }
        }
    }
}

export function focusableChoicesWithin(node: ParentNode): HTMLElement[] {
    return [...node.querySelectorAll(focusableCssSelector)]
        .filter(isHtmlElement)
        .filter(o => o.offsetParent);
}

export type selectorBasedFocusTarget = { tryToRestoreFocus: () => boolean };

export function collectFuzzyFocusSelector(): selectorBasedFocusTarget {
    let focusSelector: string[] = [];

    if (document.activeElement) {
        ConditionalLog.log("Saving currently focused element ", document.activeElement);
        let focusContext = document.activeElement instanceof Element ? document.activeElement : null;
        while (focusContext) {
            for (const selectorFactory of selectorFactories) {
                const selector = selectorFactory(focusContext);
                if (selector) {
                    focusSelector.push(selector);
                    break;
                }
            }

            focusContext = (focusContext instanceof HTMLElement ? getTooltipTargetFromWrapper(focusContext) : null)
                ?? focusContext.parentElement;
        }
    }

    return { tryToRestoreFocus: () => restoreFocus(focusSelector) };
}

type selectorFactory = (el: Element) => string | null;

const selectorFactories: selectorFactory[] = [
    el => getAttributeSelector('data-controlname', el),
    el => {
        const lijstRijSelector = getAttributeSelector('data-rowindex', el);
        if (lijstRijSelector) {
            for (let lijst = el.parentNode; lijst instanceof HTMLElement; lijst = lijst.parentNode) {
                const modTypeSelector = getModuleTypeSelector(lijst);
                if (modTypeSelector) {
                    return modTypeSelector + " " + lijstRijSelector;
                }
            }
        }
        return null;
    },
    el => getAttributeSelector('id', el),
    el => getModuleTypeSelector(el),
    el => getAttributeSelector('data-smoke_marker', el),
];

function getModuleTypeSelector(el: Element) {
    const typeSel = getAttributeSelector('data-moduletype', el);
    const moduleSel = getAttributeSelector('data-modulename', el);
    return typeSel && moduleSel ? typeSel + moduleSel : null;
}

function getAttributeSelector(attributeName: string, element: Element) {
    const attrValue = element.getAttribute(attributeName);
    return attrValue ? `[${attributeName}=\'${attrValue.replace('\'', '\\\'')}\']` : null;
}

function isHtmlElement(el: Element | null): el is HTMLElement {
    return el instanceof HTMLElement;
}

type elInScope = { target: HTMLElement; scope: HTMLElement; };
function isElInScope(el: any): el is elInScope {
    return !!el && el.scope instanceof HTMLElement && el.target instanceof HTMLElement;
}

const focusableCssSelector = ':is(:any-link, :enabled, [tabindex]):not([tabindex="-1"]):not(:disabled)';
const eventuallyFocusable = `:is(:any-link, :enabled, [tabindex],[${delayedFocusableAttr}]):not([tabindex="-1"]):not(:disabled)`;

function restoreFocus(focusSelector: string[]): boolean {
    var autoFocusableElements = Array.from(document.querySelectorAll("[autofocus]")).filter(isHtmlElement);
    if (autoFocusableElements.length == 1) {
        ConditionalLog.log("restoring focus to autofocusable element", autoFocusableElements[0]);
        autoFocusableElements[0].focus();
        return true;
    }

    ConditionalLog.log("Focus selector:", focusSelector);

    if (focusSelector.length === 0) {
        return true;
    }

    let candidates: elInScope[] | null = null;
    let debugSelIdx = 0;
    for (const selector of focusSelector) {
        const newCandidates: elInScope[] = candidates === null
            ? Array.from(document.querySelectorAll(selector))
                .filter(isHtmlElement)
                .map(el => ({ target: el, scope: el }))
            : candidates
                .map(o => ({ ...o, scope: o.scope.parentElement }))
                .filter(isElInScope)
                .map(o => ({ ...o, scope: o.scope!.closest(selector) }))
                .filter(isElInScope);

        if (newCandidates.length > 0) {
            candidates = newCandidates;
            if (candidates.length === 1) {
                break;
            }
        }

        debugSelIdx++;
    }
    if (candidates === null || candidates.length !== 1) {
        ConditionalLog.log("refocus failed due to ambiguity", debugSelIdx, focusSelector, candidates?.length);
        return false;
    }

    const elemToFocusWithin = candidates[0];
    let target = elemToFocusWithin.target;
    while (true) {
        if (target.matches(eventuallyFocusable) && !target.classList.contains(ProgCss.MenuControl_tab)) {
            delayedFocus.eventuallyFocus(target);
            ConditionalLog.log("refocus SUCCESS", debugSelIdx, focusSelector, elemToFocusWithin, target);
            return true;
        }

        let candidateCount = 0;
        let bestCandidate: HTMLElement | null = null;
        for (const focusCandidate of target.querySelectorAll(eventuallyFocusable)) {
            if (isHtmlElement(focusCandidate) && focusCandidate.offsetParent) {
                bestCandidate = candidateCount === 0 ? focusCandidate : bestCandidate;
                candidateCount++;
                if (candidateCount > 4)
                    break;
            }
        }

        if (bestCandidate) {
            if (candidateCount > 4 && target.offsetHeight > 400) {
                ConditionalLog.log("refocus within failed due to suspiciously large sub-control count", debugSelIdx, focusSelector, elemToFocusWithin, bestCandidate, target.offsetHeight);
                return false;
            } else if (bestCandidate.closest('[inert]')) {
                ConditionalLog.log("refocus within failed due to inert container", debugSelIdx, focusSelector, elemToFocusWithin, bestCandidate);
                return false;
            }
            delayedFocus.eventuallyFocus(bestCandidate);
            ConditionalLog.log("refocus SUCCESS (within)", debugSelIdx, focusSelector, elemToFocusWithin, bestCandidate, target.offsetHeight);
            return true;
        }

        if (target != elemToFocusWithin.scope && target.parentElement) {
            target = target.parentElement;
        } else {
            ConditionalLog.log("refocus failed due to non-selectability", debugSelIdx, focusSelector, elemToFocusWithin, bestCandidate);
            return false;
        }
    }
}
